/**
 * Generic CSV Upload Button Component for Entities
 *
 * A reusable component for uploading CSV files to bulk create entities.
 * Supports Data Types, Clinics, Studies, Devices, and Datasets.
 */

import { useState, useRef } from 'react';
import { Button, Alert, Modal } from 'react-bootstrap';
import { apiClient } from '@/services/apiClient';
import toast from 'react-hot-toast';

interface CSVUploadResponse {
  success: boolean;
  total_rows: number;
  created: number;
  errors: string[];
  warnings: string[];
}

interface EntityCSVUploadButtonProps {
  entityType: 'data-types' | 'clinics' | 'studies' | 'devices' | 'datasets';
  onUploadComplete?: () => void; // TODO: Will be used when backend endpoints are ready
  variant?: string;
  size?: 'sm' | 'lg';
  className?: string;
  disabled?: boolean;
}

export default function EntityCSVUploadButton({
  entityType,
  onUploadComplete,
  variant = 'outline-primary',
  size = 'sm',
  className,
  disabled = false,
}: EntityCSVUploadButtonProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState<CSVUploadResponse | null>(null);

  const handleButtonClick = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.csv')) {
      toast.error('File must be a CSV file (.csv extension)');
      return;
    }

    setUploading(true);

    try {
      let response;
      if (entityType === 'data-types') {
        response = await apiClient.uploadDataTypesCSV(file);
      } else if (entityType === 'clinics') {
        response = await apiClient.uploadClinicsCSV(file);
      } else if (entityType === 'studies') {
        response = await apiClient.uploadStudiesCSV(file);
      } else if (entityType === 'devices') {
        response = await apiClient.uploadDevicesCSV(file);
      } else if (entityType === 'datasets') {
        response = await apiClient.uploadDatasetsCSV(file);
      } else {
        throw new Error(`Unknown entity type: ${entityType}`);
      }

      if (response.data && !response.error) {
        const uploadResult = response.data as CSVUploadResponse;
        setResult(uploadResult);
        setShowResult(true);

        if (uploadResult.success) {
          toast.success(`Successfully uploaded ${uploadResult.created} ${getEntityLabel()}`);
          if (onUploadComplete) {
            onUploadComplete();
          }
        } else {
          toast.error(`Upload completed with errors. Check details in the modal.`);
        }
      } else {
        throw new Error(response.error || 'Upload failed');
      }
    } catch (error) {
      console.error('CSV upload error:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      toast.error(errorMessage);
      setResult({
        success: false,
        total_rows: 0,
        created: 0,
        errors: [errorMessage],
        warnings: [],
      });
      setShowResult(true);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleCloseResult = () => {
    setShowResult(false);
    setResult(null);
  };

  const getCSVFormat = () => {
    switch (entityType) {
      case 'data-types':
        return 'name,description\nECG,Electrocardiogram data\nBP,Blood pressure measurements\nHR,Heart rate data';
      case 'clinics':
        return 'clinic_id,name,description,location,is_active\nCLINIC-001,Main Clinic,Primary clinic location,Toronto,true\nCLINIC-002,Secondary Clinic,Secondary location,Montreal,true';
      case 'studies':
        return 'study_id,name,description,principal_investigator,start_date,end_date,is_active\nSTUDY-001,Cardiac Study,Study on cardiac health,Dr. Smith,2024-01-01,2024-12-31,true';
      case 'devices':
        return 'device_id,name,model,manufacturer\nDEV-001,ECG Monitor,Model-X,Manufacturer-A\nDEV-002,BP Monitor,Model-Y,Manufacturer-B';
      case 'datasets':
        return 'name,description,data_type_id,device_id,clinic_id,study_id,start_time,end_time\nDataset 1,Description,1,1,1,1,2024-01-01T00:00:00Z,2024-12-31T23:59:59Z';
      default:
        return '';
    }
  };

  const getEntityLabel = () => {
    switch (entityType) {
      case 'data-types':
        return 'data types';
      case 'clinics':
        return 'clinics';
      case 'studies':
        return 'studies';
      case 'devices':
        return 'devices';
      case 'datasets':
        return 'datasets';
      default:
        return 'items';
    }
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <Button
        variant={variant}
        size={size}
        className={className}
        onClick={handleButtonClick}
        disabled={uploading || disabled}
        title={disabled ? 'CSV upload not available' : `Upload CSV to bulk create ${getEntityLabel()}`}
      >
        {uploading ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
            Uploading...
          </>
        ) : (
          <>
            <i className="fas fa-file-csv me-2" />
            Upload CSV
          </>
        )}
      </Button>

      {/* Result Modal */}
      <Modal show={showResult} onHide={handleCloseResult} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {result?.success ? (
              <>
                <i className="fas fa-check-circle text-success me-2" />
                Upload Successful
              </>
            ) : (
              <>
                <i className="fas fa-exclamation-triangle text-danger me-2" />
                Upload Failed
              </>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {result && (
            <>
              {/* Summary */}
              <div className="mb-3">
                <h6>Summary</h6>
                <ul className="list-unstyled mb-0">
                  <li>
                    <strong>Total rows processed:</strong> {result.total_rows}
                  </li>
                  <li>
                    <strong>{getEntityLabel()} created:</strong> {result.created}
                  </li>
                </ul>
              </div>

              {/* Warnings */}
              {result.warnings.length > 0 && (
                <Alert variant="warning" className="mb-3">
                  <Alert.Heading className="h6">
                    <i className="fas fa-exclamation-triangle me-2" />
                    Warnings ({result.warnings.length})
                  </Alert.Heading>
                  <ul className="mb-0" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {result.warnings.map((warning, idx) => (
                      <li key={idx}><small>{warning}</small></li>
                    ))}
                  </ul>
                </Alert>
              )}

              {/* Errors */}
              {result.errors.length > 0 && (
                <Alert variant="danger" className="mb-0">
                  <Alert.Heading className="h6">
                    <i className="fas fa-times-circle me-2" />
                    Errors ({result.errors.length})
                  </Alert.Heading>
                  <ul className="mb-0" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {result.errors.map((error, idx) => (
                      <li key={idx}><small>{error}</small></li>
                    ))}
                  </ul>
                </Alert>
              )}

              {/* Format Help */}
              {!result.success && (
                <div className="mt-3">
                  <small className="text-muted">
                    <strong>Expected CSV format:</strong>
                    <pre className="bg-light p-2 mt-2" style={{ fontSize: '0.85em' }}>
                      {getCSVFormat()}
                    </pre>
                  </small>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseResult}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
